import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import { DEV_MODE } from '@/lib/config';

interface AppLayoutProps {
  children: ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const navigate = useNavigate();
  const setMemberships = useOrgStore((state) => state.setMemberships);

  useEffect(() => {
    // Check authentication
    const checkAuth = async () => {
      // Skip auth check in developer mode
      if (DEV_MODE) {
        setMemberships([]);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/auth');
        return;
      }

      // Load memberships from database
      const { data: memberships, error } = await supabase
        .from('memberships')
        .select(`
          org_id,
          role,
          organization:organizations(*)
        `)
        .eq('user_id', session.user.id);

      if (error) {
        console.error('Failed to load memberships:', error);
        return;
      }

      if (!memberships || memberships.length === 0) {
        // No memberships - redirect to onboarding
        navigate('/onboard');
        return;
      }

      setMemberships(memberships as any);
    };

    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, setMemberships]);

  return (
    <div className="relative min-h-screen bg-background overflow-hidden">
      <AnimatedBackground opacity={0.1} />
      <Topbar />
      <div className="flex">
        <Sidebar />
        <main className="relative z-10 flex-1 p-6 ml-64">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
